/**
 * QuickActionsBar Component
 * Quick action links for leave management
 */

import { ROUTES } from "@features/rh/config/constants";
import { Link } from "@tanstack/react-router";
import { BarChart3, Calendar, CalendarPlus, Clock, List, Settings } from "lucide-react";
import { cn } from "@/lib/utils/cn";

interface QuickActionsBarProps {
  /** Additional CSS classes */
  className?: string;
  /** Show labels (default: true) */
  showLabels?: boolean;
  /** Compact mode for inline use */
  compact?: boolean;
}

/**
 * Quick action definition
 */
interface QuickAction {
  /** Unique identifier */
  id: string;
  /** Label text */
  label: string;
  /** Description */
  description?: string;
  /** Target route path */
  to: string;
  /** Lucide icon component */
  icon: React.ComponentType<{ className?: string }>;
  /** Icon background color */
  iconBg: string;
  /** Icon text color */
  iconColor: string;
}

/**
 * Default quick actions for conges dashboard
 */
const DEFAULT_ACTIONS: QuickAction[] = [
  {
    id: "new-request",
    label: "Nouvelle demande",
    description: "Soumettre une demande d'absence",
    to: ROUTES.RH.CONGES.NEW_REQUEST,
    icon: CalendarPlus,
    iconBg: "bg-info/10",
    iconColor: "text-info",
  },
  {
    id: "my-requests",
    label: "Mes demandes",
    description: "Consulter mes congés",
    to: ROUTES.RH.CONGES.LIST,
    icon: List,
    iconBg: "bg-primary/10",
    iconColor: "text-primary",
  },
  {
    id: "my-calendar",
    label: "Mon calendrier",
    description: "Voir mon planning",
    to: "/planning/me",
    icon: Calendar,
    iconBg: "bg-success/10",
    iconColor: "text-success",
  },
  {
    id: "my-timesheets",
    label: "Feuilles de temps",
    description: "Déclarer mes heures",
    to: ROUTES.RH.POINTAGE.ME,
    icon: Clock,
    iconBg: "bg-warning/10",
    iconColor: "text-warning",
  },
  {
    id: "my-stats",
    label: "Mes statistiques",
    description: "Voir mes bilans",
    to: "/rh/conges/stats",
    icon: BarChart3,
    iconBg: "bg-secondary/10",
    iconColor: "text-secondary",
  },
  {
    id: "settings",
    label: "Paramètres",
    description: "Gérer mes préférences",
    to: "/profile/conges",
    icon: Settings,
    iconBg: "bg-base-200",
    iconColor: "text-base-content/70",
  },
];

/**
 * Quick actions bar component
 * Displays a grid of quick action links styled like QuickAccessCard
 */
export function QuickActionsBar({
  className = "",
  showLabels = true,
  compact = false,
}: QuickActionsBarProps) {
  const actions = DEFAULT_ACTIONS;

  if (compact) {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        {actions.slice(0, 4).map((action) => (
          <Link
            key={action.id}
            to={action.to}
            className={cn(
              "btn btn-ghost btn-sm gap-2",
              "hover:bg-base-200/50",
              "text-base-content/70 hover:text-base-content",
            )}
            title={action.label}
          >
            <action.icon className="w-4 h-4" />
            {showLabels && <span className="text-xs">{action.label}</span>}
          </Link>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6",
        "gap-3 md:gap-4",
        className,
      )}
    >
      {actions.map((action, index) => (
        <QuickActionCard key={action.id} action={action} delay={index * 50} />
      ))}
    </div>
  );
}

/**
 * Single quick action card
 */
function QuickActionCard({ action, delay = 0 }: { action: QuickAction; delay?: number }) {
  return (
    <Link
      to={action.to}
      className={cn(
        "card bg-base-200 shadow-sm",
        "border border-base-300/60",
        "hover:shadow-md hover:border-primary/30",
        "hover:-translate-y-0.5",
        "transition-all duration-300",
        "group",
        "animate-fade-in",
      )}
      style={{
        animationDelay: `${delay}ms`,
        animationFillMode: "both",
      }}
    >
      <div className="card-body p-4 flex-col items-center text-center gap-3">
        {/* Icon */}
        <div
          className={cn(
            "w-12 h-12 rounded-xl flex items-center justify-center",
            "group-hover:scale-110 transition-transform duration-300",
            "shadow-sm",
            action.iconBg,
            action.iconColor,
          )}
        >
          <action.icon className="w-6 h-6" />
        </div>

        {/* Label */}
        <div>
          <h4 className="card-title text-sm mb-0 group-hover:text-primary transition-colors">
            {action.label}
          </h4>
          {action.description && (
            <p className="text-xs text-base-content/50 mt-1 line-clamp-2">{action.description}</p>
          )}
        </div>
      </div>
    </Link>
  );
}

/**
 * Compact horizontal quick actions bar
 * For use in headers or compact layouts
 */
export function QuickActionsBarHorizontal({ className = "" }: { className?: string }) {
  const primaryActions = DEFAULT_ACTIONS.slice(0, 4);

  return (
    <div className={cn("flex flex-wrap items-center gap-2", className)}>
      {primaryActions.map((action) => (
        <Link
          key={action.id}
          to={action.to}
          className={cn("btn btn-outline btn-sm gap-2", "hover:bg-base-200/50", "group")}
        >
          <action.icon
            className={cn("w-4 h-4 transition-transform group-hover:scale-110", action.iconColor)}
          />
          <span>{action.label}</span>
        </Link>
      ))}
    </div>
  );
}

/**
 * Quick action button for inline use
 */
export function QuickActionButton({
  action,
  className = "",
}: {
  action: QuickAction;
  className?: string;
}) {
  return (
    <Link
      to={action.to}
      className={cn(
        "btn btn-sm gap-2",
        "hover:-translate-y-0.5",
        "transition-all duration-200",
        action.iconBg,
        action.iconColor,
        className,
      )}
    >
      <action.icon className="w-4 h-4" />
      {action.label}
    </Link>
  );
}
